// Minimal structural view of a TanStack `FieldApi` — only what the binding
// components read. The real `<form.Field>` field is assignable to this.
export type BoundFieldMeta = {
  errors: readonly unknown[];
  isBlurred: boolean;
  // TanStack's per-cause error map, narrowed to its submit slot: populated when
  // a submit validator rejects the field or a server `fieldErrors` outcome is
  // routed to it. Display gating reads presence, not shape.
  errorMap: { onSubmit?: unknown };
};

export type BoundField<T> = {
  state: { value: T; meta: BoundFieldMeta };
  handleChange: (value: T) => void;
  handleBlur: () => void;
};

// Standard Schema / Zod issues are `{ message }` objects; native validators may
// yield plain strings. Surface the first readable message either way.
export const firstErrorMessage = (
  errors: readonly unknown[],
): string | undefined => {
  for (const err of errors) {
    if (typeof err === "string") {
      return err;
    }
    if (typeof err === "object" && err !== null && "message" in err) {
      const message = err.message;
      if (typeof message === "string") {
        return message;
      }
    }
  }
  return undefined;
};

// Display gating (standard form UX): validation runs live, but a field's error
// stays hidden until the user leaves the field or a submit attempt routes
// errors to it — so submit reveals problems in fields the user never visited,
// while a half-typed value is never painted red. Blur, not touch: controlled
// primitives can emit a change at mount (e.g. DateField's initial
// `onValidChange`), which would mark a pristine form touched.
export const visibleFieldError = (meta: BoundFieldMeta): string | undefined =>
  meta.isBlurred || meta.errorMap.onSubmit !== undefined
    ? firstErrorMessage(meta.errors)
    : undefined;

// Non-blocking presence cue: a required field blurred, or still empty after a
// submit attempt, shows a required message WITHOUT a schema validator — a field
// validator error blocks handleSubmit, which would gate section saves. The
// submit signal is passed in rather than read from `meta.errorMap.onSubmit`:
// these fields carry format-only validators that accept an empty value, so an
// empty required field never populates that slot — only a blocking validator
// would, which is exactly what this cue avoids. Format errors win, so callers
// do `visibleFieldError(meta) ?? visibleRequiredError(...)`.
export const REQUIRED_FIELD_MESSAGE = "This field is required.";

export const visibleRequiredError = (
  meta: BoundFieldMeta,
  isEmpty: boolean,
  required: boolean | undefined,
  submitted: boolean,
): string | undefined =>
  required && isEmpty && (meta.isBlurred || submitted)
    ? REQUIRED_FIELD_MESSAGE
    : undefined;
